import React, { useEffect, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface PopoutWindowProps {
  /** Content to render in the popout window */
  children: ReactNode;
  /** Title for the popout window */
  title: string;
  /** Width of the popout window in pixels */
  width?: number;
  /** Height of the popout window in pixels */
  height?: number;
  /** Callback when the window is closed */
  onClose?(): void;
}

/**
 * Component that renders children in a separate browser window (popout)
 *
 * Useful for multi-monitor setups where users want to view transcript or video
 * on a separate display while working with segments on the main display
 *
 * The component creates a new window and copies styles from the parent window
 * to ensure consistent appearance
 *
 * @param props - PopoutWindow component props
 * @returns Portal to the new window or null if window creation failed
 */
const PopoutWindow: React.FC<PopoutWindowProps> = ({
  children,
  title,
  width = 800,
  height = 600,
  onClose,
}) => {
  const [containerEl] = useState(() => document.createElement('div'));
  const windowRef = useRef<Window | null>(null);

  useEffect(() => {
    // Create new window
    const newWindow = window.open(
      '',
      '',
      `width=${width},height=${height},left=100,top=100,resizable=yes,scrollbars=yes`,
    );

    if (!newWindow) {
      console.error('Failed to open popout window. Check if popups are blocked.');
      onClose?.();
      return;
    }

    windowRef.current = newWindow;

    // Set window title
    newWindow.document.title = title;

    // Copy styles from parent window to child window
    const parentStyles = Array.from(document.styleSheets);
    parentStyles.forEach((styleSheet) => {
      try {
        if (styleSheet.href) {
          // External stylesheet
          const link = newWindow.document.createElement('link');
          link.rel = 'stylesheet';
          link.href = styleSheet.href;
          newWindow.document.head.appendChild(link);
        } else if (styleSheet.cssRules) {
          // Inline stylesheet
          const style = newWindow.document.createElement('style');
          Array.from(styleSheet.cssRules).forEach((rule) => {
            style.appendChild(newWindow.document.createTextNode(rule.cssText));
          });
          newWindow.document.head.appendChild(style);
        }
      } catch (e) {
        console.warn('Could not copy stylesheet:', e);
      }
    });

    // Establish a real height chain so nested `height: 100%` / flex layouts work.
    // Without this, portal content often gets 0 height and virtualized lists never mount.
    const htmlEl = newWindow.document.documentElement;
    htmlEl.style.height = '100%';
    htmlEl.style.margin = '0';
    newWindow.document.body.style.margin = '0';
    newWindow.document.body.style.padding = '0';
    newWindow.document.body.style.height = '100%';
    newWindow.document.body.style.overflow = 'hidden';
    newWindow.document.body.style.display = 'flex';
    newWindow.document.body.style.flexDirection = 'column';

    containerEl.style.flex = '1';
    containerEl.style.minHeight = '0';
    containerEl.style.width = '100%';
    containerEl.style.display = 'flex';
    containerEl.style.flexDirection = 'column';

    // Add container to new window body
    newWindow.document.body.appendChild(containerEl);

    // Handle window close by user
    const handleBeforeUnload = (): void => {
      onClose?.();
    };

    newWindow.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on unmount - close the window
    return () => {
      if (windowRef.current && !windowRef.current.closed) {
        windowRef.current.removeEventListener('beforeunload', handleBeforeUnload);
        windowRef.current.close();
      }
    };
  }, [title, width, height, containerEl, onClose]);

  // Render children into the container element in the new window
  return createPortal(children, containerEl);
};

export default PopoutWindow;
